import { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useNavigate, useLocation } from 'react-router-dom';
import L from 'leaflet';
import { Search, MapPin, X, Loader, Phone, Info } from 'lucide-react';
import StickyNavHeader from '../components/header/StickyNavHeader';
import Footer from '../components/footer/Footer';
import CategoryScrollBar from '../components/map/CategoryScrollBar';
import 'leaflet/dist/leaflet.css';
import { fetchBusinessesForMap } from '../domain/useCases/Business/fetchBusinessesForMap.js';
import { searchBusinessesForMap } from '../domain/useCases/Business/searchBusinessesForMap.js';
import { searchBusinessesByLocationForMap } from '../domain/useCases/Business/searchBusinessesByLocationForMap.js';
import { filterBusinessesByCategoryForMap } from '../domain/useCases/Business/filterBusinessesByCategoryForMap.js';
import { fetchExploreCategories } from '../domain/useCases/Business/fetchExploreCategories.js';
import { sortByDistance } from '../domain/utils/Geo/sortByDistance.js';
import { NEPAL_CENTER } from '../domain/utils/Geo/NEPAL_CENTER.js';
import { searchLocation } from '../domain/useCases/Location/searchLocation.js';

// Custom smaller marker icon
const customIcon = L.icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [20, 32],
  iconAnchor: [10, 32],
  popupAnchor: [0, -32],
  shadowSize: [41, 41],
  shadowAnchor: [12, 41],
});

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Wrapper for fetchExploreCategories that normalizes the response
const loadExploreCategories = async () => {
  try {
    const result = await fetchExploreCategories();
    return Array.isArray(result) ? result : result.data || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};
const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      const zoom = center.zoom || 7; // Use provided zoom or default to 7
      map.setView([center.lat, center.lng], zoom);
    }
  }, [center, map]);
  return null;
};

// ============================================
// MAP PAGE COMPONENT
// ============================================
const MapPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: NEPAL_CENTER.lat, lng: NEPAL_CENTER.lng, zoom: 7 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showAllData, setShowAllData] = useState(false);
  const [mapType, setMapType] = useState('street'); // 'street' or 'satellite'
  const [searchDistance] = useState(80000); // 80km default (fixed, no UI)
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermissionAsked, setLocationPermissionAsked] = useState(false);
  const [nearbyLoaded, setNearbyLoaded] = useState(false);
  const mapRef = useRef(null);
  const sidebarRef = useRef(null);
  const categoryRestoredRef = useRef(false);
  const initializedRef = useRef(false);
  const filterEffectInitializedRef = useRef(false);
  const locationInputRef = useRef(null);

  // Helper function to sort by user location if available, otherwise by Nepal center
  const sortByRelevantLocation = (businesses) => {
    if (userLocation) {
      return sortByDistance(businesses, userLocation.lat, userLocation.lng);
    }
    return sortByDistance(businesses, NEPAL_CENTER.lat, NEPAL_CENTER.lng);
  };

  // Initialize: Load all businesses and categories on component mount
  useEffect(() => {
    if (initializedRef.current) return; // Prevent double initialization in React Strict Mode
    initializedRef.current = true;

    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    const initializeMap = async () => {
      setLoading(true);
      setError(null);
      setCurrentPage(1);
      try {
        console.log('Fetching businesses and categories from API...');

        // Fetch categories
        const categoriesData = await loadExploreCategories();
        setCategories(categoriesData);
        console.log('Categories loaded:', categoriesData);

        // Load only first 100 items initially for faster load
        const result = await fetchBusinessesForMap({ page: '1', take: '100' });
        console.log('Businesses loaded:', result.businesses);

        // Center map on Nepal (not on user location)
        const nepalCenter = NEPAL_CENTER;
        setMapCenter({ lat: nepalCenter.lat, lng: nepalCenter.lng, zoom: 7 });

        // Sort by distance from Nepal center (or user location if available)
        const sortedBusinesses = sortByDistance(result.businesses, nepalCenter.lat, nepalCenter.lng);
        setFilteredEntities(sortedBusinesses);
        setHasMore(result.hasNext);
      } catch (err) {
        console.error('Initialization error:', err);
        setError('Failed to load map data');
      } finally {
        setLoading(false);
      }
    };

    initializeMap();

    // Request user location permission
    if (!locationPermissionAsked && navigator.geolocation) {
      setLocationPermissionAsked(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('User location obtained:', { latitude, longitude });
          setUserLocation({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.log('Geolocation error:', error.message);
          // Silently fail - user can still use the map
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    }
  }, []);

  // Restore category from location state when coming back from entity profile
  useEffect(() => {
    if (categoryRestoredRef.current) return; // Prevent duplicate restoration

    const incomingCategory = location.state?.selectedCategory;
    if (incomingCategory && categories.length > 0) {
      console.log('Restoring category from location state:', incomingCategory);
      categoryRestoredRef.current = true;
      setSelectedCategory(incomingCategory);
    }
  }, [location.state?.selectedCategory, categories.length]);

  // Auto-load nearby businesses when user location is obtained
  useEffect(() => {
    if (userLocation && !nearbyLoaded && !selectedLocation) {
      setNearbyLoaded(true);
      setLoading(true);
      setCurrentPage(1);

      const loadNearby = async () => {
        try {
          console.log('Loading nearby businesses for user location:', userLocation);
          const result = await searchBusinessesByLocationForMap(
            userLocation.lat,
            userLocation.lng,
            searchDistance,
            [],
            1,
            100
          );
          // Sort by distance from user location
          const sortedByDistance = sortByDistance(result.businesses, userLocation.lat, userLocation.lng);
          setFilteredEntities(sortedByDistance);
          setHasMore(result.hasNext);
          setMapCenter({ lat: userLocation.lat, lng: userLocation.lng, zoom: 12 });
          setError(null);
        } catch (err) {
          console.error('Nearby search error:', err);
          setError('Failed to load nearby businesses');
        } finally {
          setLoading(false);
        }
      };

      loadNearby();
    }
  }, [userLocation, nearbyLoaded, selectedLocation, searchDistance]);

  // Handle location search suggestions
  useEffect(() => {
    // Don't search if a location is already selected
    if (selectedLocation) {
      return;
    }

    const timer = setTimeout(async () => {
      if (locationQuery.trim().length > 2) {
        try {
          console.log('🔍 Searching for location:', locationQuery);
          const results = await searchLocation(locationQuery);
          console.log('✅ Location search results:', results);
          
          // Handle both array and object responses
          const locationList = Array.isArray(results) ? results : results?.data || [];
          console.log('📍 Processed location list:', locationList);
          
          setLocationSuggestions(locationList);
          setShowLocationSuggestions(locationList.length > 0);
          
          if (locationList.length === 0) {
            console.warn('⚠️ No locations found for:', locationQuery);
          }
        } catch (err) {
          console.error('❌ Location search error:', err);
          setLocationSuggestions([]);
          setShowLocationSuggestions(false);
        }
      } else {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [locationQuery, selectedLocation]);

  // Handle location selection
  const handleLocationSelect = async (location) => {
    // Set location query first to display the selected location name
    const locationName = location.name || location.display_name || '';
    setLocationQuery(locationName);
    setSelectedLocation(location);
    setLocationSuggestions([]); // Clear suggestions
    setShowLocationSuggestions(false); // Hide dropdown after selection
    setLoading(true);
    setCurrentPage(1);

    try {
      // Handle different coordinate formats (Nominatim uses lat/lon)
      const lat = parseFloat(location.lat || location.latitude);
      const lng = parseFloat(location.lon || location.lng || location.longitude);

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        throw new Error('Invalid location coordinates');
      }

      // Update map center and zoom to the selected location
      setMapCenter({ lat, lng, zoom: 12 });

      // Search businesses by location
      let result;
      if (selectedCategory) {
        const selectedCat = categories.find(c => c.id === selectedCategory);
        const categoryIds = selectedCat?.customFilter?.categoryIds || [];
        result = await searchBusinessesByLocationForMap(
          lat,
          lng,
          searchDistance,
          categoryIds,
          1,
          100
        );
      } else {
        result = await searchBusinessesByLocationForMap(lat, lng, searchDistance, [], 1, 100);
      }

      setFilteredEntities(result.businesses);
      setHasMore(result.hasNext);
      setError(null);
    } catch (err) {
      console.error('Location search error:', err);
      setError('Failed to search businesses by location');
    } finally {
      setLoading(false);
    }
  };

  // Handle clear location search
  const handleClearLocation = async () => {
    setLocationQuery('');
    setSelectedLocation(null);
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedEntity(null);
    setShowAllData(false);
    categoryRestoredRef.current = false;
    const nepalCenter = NEPAL_CENTER;
    setMapCenter({ lat: nepalCenter.lat, lng: nepalCenter.lng, zoom: 7 });
    setCurrentPage(1);
    setLoading(true);
    try {
      const result = await fetchBusinessesForMap({ page: '1', take: '100' });
      const sortedResults = sortByRelevantLocation(result.businesses);
      setFilteredEntities(sortedResults);
      setHasMore(result.hasNext);
      setError(null);
    } catch (err) {
      console.error('Load error:', err);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  // Handle search and category filter with debounce
  useEffect(() => {
    // Skip first render to avoid duplicate API calls during initialization
    if (!filterEffectInitializedRef.current) {
      filterEffectInitializedRef.current = true;
      return;
    }

    const timer = setTimeout(async () => {
      const nepalCenter = NEPAL_CENTER;

      // If a location is selected, apply category filter to location search
      if (selectedLocation) {
        setLoading(true);
        setCurrentPage(1);
        try {
          const lat = parseFloat(selectedLocation.lat || selectedLocation.latitude);
          const lng = parseFloat(selectedLocation.lon || selectedLocation.lng || selectedLocation.longitude);

          if (isNaN(lat) || isNaN(lng)) {
            throw new Error('Invalid location coordinates');
          }

          let result;
          if (selectedCategory) {
            const selectedCat = categories.find(c => c.id === selectedCategory);
            const categoryIds = selectedCat?.customFilter?.categoryIds || [];
            result = await searchBusinessesByLocationForMap(
              lat,
              lng,
              searchDistance,
              categoryIds,
              1,
              100
            );
          } else {
            result = await searchBusinessesByLocationForMap(lat, lng, searchDistance, [], 1, 100);
          }

          setFilteredEntities(result.businesses);
          setHasMore(result.hasNext);
          setError(null);
        } catch (err) {
          console.error('Location search error:', err);
          setError('Failed to search businesses by location');
        } finally {
          setLoading(false);
        }
        return;
      }

      if (searchQuery.trim().length > 0) {
        setLoading(true);
        setCurrentPage(1);
        try {
          console.log('Searching for:', searchQuery);
          let result;

          // If category is selected, search within that category
          if (selectedCategory) {
            const selectedCat = categories.find(c => c.id === selectedCategory);
            if (selectedCat && selectedCat.customFilter && selectedCat.customFilter.categoryIds) {
              // Search with category filter
              const params = {
                page: '1',
                take: '100',
                name: searchQuery,
                categoryIds: selectedCat.customFilter.categoryIds,
              };
              result = await fetchBusinessesForMap(params);
            } else {
              result = await searchBusinessesForMap(searchQuery, 1, 100);
            }
          } else {
            result = await searchBusinessesForMap(searchQuery, 1, 100);
          }

          console.log('Search results count:', result.businesses.length);

          // Sort by distance from user location or Nepal center
          const sortedResults = sortByRelevantLocation(result.businesses);
          setFilteredEntities(sortedResults);
          setHasMore(result.hasNext);

          if (result.businesses.length === 0) {
            setError('No businesses found matching your search');
          } else {
            setError(null);
          }
        } catch (err) {
          console.error('Search error:', err);
          setError('Search failed');
        } finally {
          setLoading(false);
        }
      } else if (selectedCategory) {
        // If search is cleared but category is selected, show category results
        setLoading(true);
        setCurrentPage(1);
        try {
          const selectedCat = categories.find(c => c.id === selectedCategory);
          if (selectedCat && selectedCat.customFilter && selectedCat.customFilter.categoryIds) {
            const result = await filterBusinessesByCategoryForMap(selectedCat.customFilter.categoryIds, 1, 100);
            const sortedResults = sortByRelevantLocation(result.businesses);
            setFilteredEntities(sortedResults);
            setHasMore(result.hasNext);
            setError(null);
          }
        } catch (err) {
          console.error('Category filter error:', err);
          setError('Filter failed');
        } finally {
          setLoading(false);
        }
      } else {
        // Load all businesses - only first 100 initially
        setLoading(true);
        setCurrentPage(1);
        try {
          const result = await fetchBusinessesForMap({ page: '1', take: '100' });
          const sortedResults = sortByRelevantLocation(result.businesses);
          setFilteredEntities(sortedResults);
          setHasMore(result.hasNext);
          setError(null);
        } catch (err) {
          console.error('Load error:', err);
          setError('Failed to load businesses');
        } finally {
          setLoading(false);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, selectedLocation, categories, searchDistance]);

  const handleEntityClick = (entity) => {
    setSelectedEntity(entity);
    setMapCenter({ lat: entity.lat, lng: entity.lng });

    // Check if entity is in the current list
    const isEntityInList = filteredEntities.some(e => e.id === entity.id);

    if (isEntityInList) {
      // Scroll to the selected entity in the sidebar
      if (sidebarRef.current) {
        setTimeout(() => {
          const entityElement = document.getElementById(`entity-${entity.id}`);
          if (entityElement) {
            entityElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } else {
      // If entity is not in list, add it to the top
      setFilteredEntities(prev => [entity, ...prev]);
      setTimeout(() => {
        const entityElement = document.getElementById(`entity-${entity.id}`);
        if (entityElement && sidebarRef.current) {
          entityElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleViewProfile = (entity) => {
    console.log('Navigating with entity:', {
      name: entity.name,
      tag: entity.tag,
      id: entity.id
    });
    navigate(`/entity/${entity.tag}`, {
      state: {
        categoryId: selectedCategory,
        categoryName: selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : null,
        fromMap: true
      }
    });
  };

  const handleClearFilters = async () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedEntity(null);
    setShowAllData(false);
    categoryRestoredRef.current = false; // Reset ref when clearing filters
    const nepalCenter = NEPAL_CENTER;
    setMapCenter({ lat: nepalCenter.lat, lng: nepalCenter.lng, zoom: 7 });
    setCurrentPage(1);
    setLoading(true);
    try {
      const result = await fetchBusinessesForMap({ page: '1', take: '100' });
      const sortedResults = sortByRelevantLocation(result.businesses);
      setFilteredEntities(sortedResults);
      setHasMore(result.hasNext);
      setError(null);
    } catch (err) {
      console.error('Load error:', err);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleShowAll = async () => {
    setShowAllData(true);
    setLoading(true);
    const nepalCenter = NEPAL_CENTER;

    try {
      let result;

      if (selectedCategory) {
        // Show all in selected category
        const selectedCat = categories.find(c => c.id === selectedCategory);
        if (selectedCat && selectedCat.customFilter && selectedCat.customFilter.categoryIds) {
          result = await filterBusinessesByCategoryForMap(selectedCat.customFilter.categoryIds, 1, 10000);
        } else {
          result = { businesses: [], hasNext: false };
        }
      } else {
        // Show all businesses
        result = await fetchBusinessesForMap({ page: '1', take: '10000' });
      }

      const sortedResults = sortByRelevantLocation(result.businesses);
      setFilteredEntities(sortedResults);
      setHasMore(result.hasNext);
      setCurrentPage(1);
      setError(null);
    } catch (err) {
      console.error('Show all error:', err);
      setError('Failed to load all data');
    } finally {
      setLoading(false);
    }
  };

  const handleShowLess = async () => {
    setShowAllData(false);
    setLoading(true);
    const nepalCenter = NEPAL_CENTER;

    try {
      let result;

      if (selectedCategory) {
        // Show paginated category results
        const selectedCat = categories.find(c => c.id === selectedCategory);
        if (selectedCat && selectedCat.customFilter && selectedCat.customFilter.categoryIds) {
          result = await filterBusinessesByCategoryForMap(selectedCat.customFilter.categoryIds, 1, 100);
        } else {
          result = { businesses: [], hasNext: false };
        }
      } else {
        // Show paginated all businesses
        result = await fetchBusinessesForMap({ page: '1', take: '100' });
      }

      const sortedResults = sortByRelevantLocation(result.businesses);
      setFilteredEntities(sortedResults);
      setHasMore(result.hasNext);
      setCurrentPage(1);
      setError(null);
    } catch (err) {
      console.error('Show less error:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;

    const nepalCenter = NEPAL_CENTER;
    const nextPage = currentPage + 1;
    setIsLoadingMore(true);

    try {
      let result;

      if (searchQuery.trim() && selectedCategory) {
        // Search within category
        const selectedCat = categories.find(c => c.id === selectedCategory);
        if (selectedCat && selectedCat.customFilter && selectedCat.customFilter.categoryIds) {
          const params = {
            page: nextPage.toString(),
            take: '100',
            name: searchQuery,
            categoryIds: selectedCat.customFilter.categoryIds,
          };
          result = await fetchBusinessesForMap(params);
        } else {
          result = await searchBusinessesForMap(searchQuery, nextPage, 100);
        }
      } else if (searchQuery.trim()) {
        result = await searchBusinessesForMap(searchQuery, nextPage, 100);
      } else if (selectedCategory) {
        const selectedCat = categories.find(c => c.id === selectedCategory);
        if (selectedCat && selectedCat.customFilter && selectedCat.customFilter.categoryIds) {
          result = await filterBusinessesByCategoryForMap(selectedCat.customFilter.categoryIds, nextPage, 100);
        } else {
          result = { businesses: [], hasNext: false };
        }
      } else {
        result = await fetchBusinessesForMap({ page: String(nextPage), take: '100' });
      }

      const sortedResults = sortByRelevantLocation(result.businesses);
      setFilteredEntities(prev => [...prev, ...sortedResults]);
      setCurrentPage(nextPage);
      setHasMore(result.hasNext);
    } catch (err) {
      console.error('Load more error:', err);
      setError('Failed to load more businesses');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (!sidebarRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = sidebarRef.current;

      // If user scrolled to bottom (within 100px), load more
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoadingMore && !loading) {
        handleLoadMore();
      }
    };

    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.addEventListener('scroll', handleScroll);
      return () => sidebar.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, isLoadingMore, loading, currentPage, searchQuery, selectedCategory]);

  return (
    <div className="min-h-screen bg-black">
      <StickyNavHeader />

      <div className="w-full px-4 py-6">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-gradient-to-r from-red-500/20 to-red-600/10 border border-red-500/50 rounded-2xl p-4 text-red-300 backdrop-blur-sm shadow-lg shadow-red-500/10">
            {error}
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Map with Search/Filter Above */}
          <div className="lg:col-span-2 space-y-4">
            {/* Search and Filter Section - Location and Name only */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Location Search Bar */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-2.5 text-blue-400 w-4 h-4 pointer-events-none z-10" />
                  <input
                    ref={locationInputRef}
                    type="text"
                    placeholder="Search by location (e.g., Biratnagar)..."
                    value={locationQuery}
                    onChange={(e) => setLocationQuery(e.target.value)}
                    onFocus={() => locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                    disabled={loading}
                    className="w-full bg-white/5 backdrop-blur-md border border-white/10 hover:border-blue-500/30 rounded-xl pl-10 pr-8 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-blue-500/60 focus:bg-white/10 transition-all duration-300 disabled:opacity-50 shadow-lg"
                  />
                  {locationQuery && (
                    <button
                      onClick={handleClearLocation}
                      className="absolute right-3 top-2.5 text-white/40 hover:text-white/60 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  
                  {/* Location Suggestions Dropdown */}
                  {showLocationSuggestions && locationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-white/20 rounded-xl shadow-2xl z-50 max-h-48 overflow-y-auto">
                      {locationSuggestions.map((loc, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleLocationSelect(loc)}
                          className="w-full text-left px-4 py-2 hover:bg-blue-500/20 border-b border-white/5 last:border-b-0 text-white/80 hover:text-white transition-colors text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-blue-400 flex-shrink-0" />
                            <span>{loc.name || loc.display_name || 'Unknown Location'}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 text-green-400 w-4 h-4 pointer-events-none z-10" />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={loading}
                    className="w-full bg-white/5 backdrop-blur-md border border-white/10 hover:border-green-500/30 rounded-xl pl-10 pr-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-green-500/60 focus:bg-white/10 transition-all duration-300 disabled:opacity-50 shadow-lg"
                  />
                </div>
              </div>
            </div>

            {/* Active Filters */}
            {(searchQuery || selectedCategory || selectedLocation) && (
              <div className="flex flex-wrap gap-2 items-center">
                {selectedLocation && (
                  <div className="bg-gradient-to-r from-blue-500/30 to-cyan-500/20 border border-blue-500/50 rounded-full px-3 py-1 text-blue-300 text-xs flex items-center gap-2 backdrop-blur-sm shadow-lg shadow-blue-500/10">
                    <span>📍 {selectedLocation.name || selectedLocation.display_name}</span>
                  </div>
                )}
                {searchQuery && (
                  <div className="bg-gradient-to-r from-green-500/30 to-emerald-500/20 border border-green-500/50 rounded-full px-3 py-1 text-green-300 text-xs flex items-center gap-2 backdrop-blur-sm shadow-lg shadow-green-500/10">
                    <span>🔍 {searchQuery}</span>
                  </div>
                )}
                {selectedCategory && (
                  <div className="bg-gradient-to-r from-emerald-500/30 to-green-500/20 border border-emerald-500/50 rounded-full px-3 py-1 text-emerald-300 text-xs flex items-center gap-2 backdrop-blur-sm shadow-lg shadow-emerald-500/10">
                    {categories.find(c => c.id === selectedCategory)?.iconUrl && (
                      <img
                        src={categories.find(c => c.id === selectedCategory)?.iconUrl}
                        alt="category icon"
                        className="w-3 h-3 object-contain"
                      />
                    )}
                    <span>{categories.find(c => c.id === selectedCategory)?.name || selectedCategory}</span>
                  </div>
                )}
                <button
                  onClick={handleClearLocation}
                  className="text-white/60 hover:text-white text-xs flex items-center gap-1 transition-colors hover:bg-white/5 px-2 py-1 rounded-full"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              </div>
            )}

            {/* Map */}
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-3xl overflow-hidden h-[700px] shadow-2xl shadow-black/50 backdrop-blur-sm hover:border-green-500/30 transition-all duration-500 relative">
              {loading ? (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900/50 to-black/50">
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative w-12 h-12">
                      <Loader className="w-12 h-12 text-green-500 animate-spin absolute" />
                      <div className="w-12 h-12 border-2 border-green-500/20 rounded-full absolute"></div>
                    </div>
                    <p className="text-white/70">Loading map...</p>
                  </div>
                </div>
              ) : (
                <>
                  <MapContainer
                    center={[mapCenter.lat, mapCenter.lng]}
                    zoom={7}
                    style={{ height: '100%', width: '100%' }}
                    ref={mapRef}
                    attributionControl={true}
                  >
                    <TileLayer
                      url={mapType === 'satellite' 
                        ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                      }
                      attribution={mapType === 'satellite'
                        ? '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                        : '&copy; KAHA. All rights reserved'
                      }
                    />
                    <MapController center={mapCenter} />
                    {filteredEntities.map(entity => (
                      <Marker
                        key={entity.id}
                        position={[entity.lat, entity.lng]}
                        onClick={() => handleEntityClick(entity)}
                        icon={customIcon}
                      >
                        <Popup>
                          <div className="p-3 max-w-xs">
                            {entity.avatar && (
                              <img src={entity.avatar} alt={entity.name} className="w-full h-32 object-cover rounded mb-2" />
                            )}
                            <h3 className="font-bold text-black mb-1">{entity.name}</h3>
                            <p className="text-sm text-gray-600 mb-2">{entity.address}</p>
                            {entity.category && (
                              <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                                {entity.categoryIcon && <img src={entity.categoryIcon} alt={entity.category} className="w-4 h-4 object-contain" />}
                                {entity.category}
                              </p>
                            )}
                            {entity.contact && (
                              <p className="text-xs text-gray-600 mb-1 truncate">📞 {entity.contact}</p>
                            )}
                            {entity.rating > 0 && (
                              <p className="text-sm text-gray-600 mb-2">⭐ {entity.rating}</p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-3 items-center">
                              {entity.tags.map(tag => (
                                <span key={tag} className="bg-green-500/20 text-green-700 text-xs px-2 py-1 rounded">
                                  {tag}
                                </span>
                              ))}
                              <button
                                onClick={() => {
                                  navigate(`/entity/${entity.tag}`, {
                                    state: {
                                      categoryId: selectedCategory,
                                      categoryName: selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : null,
                                      fromMap: true
                                    }
                                  });
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1 rounded transition-colors"
                              >
                                View Profile
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>

                  {/* Categories Overlay - Top center with new scrollable component */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[9998] pointer-events-auto w-[90%] max-w-5xl">
                    <CategoryScrollBar
                      categories={categories}
                      selectedCategory={selectedCategory}
                      onCategoryChange={(categoryId) => {
                        categoryRestoredRef.current = false;
                        setSelectedCategory(categoryId);
                      }}
                    />
                  </div>

                  {/* Map Type Toggle - Overlay at top right (vertical) - positioned lower to avoid overlap */}
                  <div className="absolute top-20 right-4 z-[9999] pointer-events-auto">
                    <div className="bg-white/95 backdrop-blur-md rounded-lg shadow-2xl border border-white/30 flex flex-col overflow-hidden">
                      <button
                        onClick={() => setMapType('street')}
                        className={`px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                          mapType === 'street'
                            ? 'bg-green-500 text-white shadow-lg shadow-green-500/50'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Street
                      </button>
                      <div className="h-px bg-gray-300"></div>
                      <button
                        onClick={() => setMapType('satellite')}
                        className={`px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                          mapType === 'satellite'
                            ? 'bg-green-500 text-white shadow-lg shadow-green-500/50'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Satellite
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Column - Entities List */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-3xl h-[700px] overflow-hidden flex flex-col shadow-2xl shadow-black/50 backdrop-blur-sm hover:border-green-500/30 transition-all duration-500">
            <div className="sticky top-0 bg-gradient-to-r from-black/80 to-black/60 border-b border-white/10 z-10 px-6 py-4 backdrop-blur-md">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="text-xl font-bold text-white">
                  {selectedCategory
                    ? categories.find(c => c.id === selectedCategory)?.name || 'Entities'
                    : 'Entities'
                  } <span className="text-green-400">({filteredEntities.length})</span>
                </h2>
                {!showAllData && filteredEntities.length > 0 && (
                  <button
                    onClick={handleShowAll}
                    className="bg-gradient-to-r from-green-500/30 to-emerald-500/20 hover:from-green-500/50 hover:to-emerald-500/40 text-green-300 hover:text-green-200 text-xs font-semibold px-3 py-1 rounded-full border border-green-500/50 transition-all whitespace-nowrap"
                  >
                    Show All
                  </button>
                )}
                {showAllData && (
                  <button
                    onClick={handleShowLess}
                    className="bg-gradient-to-r from-orange-500/30 to-red-500/20 hover:from-orange-500/50 hover:to-red-500/40 text-orange-300 hover:text-orange-200 text-xs font-semibold px-3 py-1 rounded-full border border-orange-500/50 transition-all whitespace-nowrap"
                  >
                    Show Less
                  </button>
                )}
              </div>
            </div>
            <div ref={sidebarRef} className="overflow-y-auto flex-1 p-6 space-y-3">

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="relative w-8 h-8">
                    <Loader className="w-8 h-8 text-green-500 animate-spin absolute" />
                    <div className="w-8 h-8 border-2 border-green-500/20 rounded-full absolute"></div>
                  </div>
                </div>
              ) : filteredEntities.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40">No businesses found</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {filteredEntities.map(entity => (
                      <button
                        key={entity.id}
                        id={`entity-${entity.id}`}
                        onClick={() => handleViewProfile(entity)}
                        className={`w-full text-left rounded-2xl overflow-hidden transition-all duration-300 group ${selectedEntity?.id === entity.id
                          ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-2 border-green-500/60 shadow-xl shadow-green-500/20'
                          : 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-2 border-gray-700/50 hover:border-green-400/50 hover:shadow-xl hover:shadow-green-500/10'
                          }`}
                      >
                        {/* Image */}
                        <div className="relative h-48 bg-gray-700/50 overflow-hidden">
                          {entity.avatar ? (
                            <img 
                              src={entity.avatar} 
                              alt={entity.name} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                              <MapPin className="w-12 h-12 text-gray-600" />
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="p-5 bg-gradient-to-br from-gray-800/95 to-gray-900/95">
                          {/* Business Name */}
                          <h3 className="font-bold text-white text-xl mb-3 group-hover:text-green-400 transition-colors line-clamp-1">
                            {entity.name}
                          </h3>
                          
                          {/* Category */}
                          {entity.category && (
                            <div className="flex items-center gap-2 mb-3">
                              {entity.categoryIcon && (
                                <img src={entity.categoryIcon} alt={entity.category} className="w-5 h-5 object-contain" />
                              )}
                              <span className="text-green-400 font-semibold text-sm">{entity.category}</span>
                            </div>
                          )}
                          
                          {/* Address */}
                          {entity.address && (
                            <div className="flex items-start gap-2 mb-3">
                              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-gray-300 line-clamp-2">{entity.address}</p>
                            </div>
                          )}
                          
                          {/* Phone */}
                          {entity.contact && (
                            <div className="flex items-center gap-2 mb-4 min-w-0">
                              <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <p className="text-sm text-gray-300 truncate">{entity.contact}</p>
                            </div>
                          )}
                          
                          {/* Tags */}
                          {entity.tags && entity.tags.length > 0 && (
                            <div className="mb-4 flex items-center gap-2">
                              <span className="inline-block bg-green-500/20 text-green-300 text-xs font-semibold px-4 py-2 rounded-full border border-green-500/30 uppercase tracking-wider">
                                {entity.tags[0]}
                              </span>
                              {/* Info Icon with Tooltip */}
                              <div className="relative group/tooltip">
                                <Info className="w-4 h-4 text-blue-400 cursor-help" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover/tooltip:block w-48 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-gray-700 z-50">
                                  For entity claim please login.
                                  <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* View Profile Button */}
                          <div className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 text-center shadow-lg shadow-green-500/30 group-hover:shadow-green-500/50">
                            View Profile
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Load more indicator */}
                  {isLoadingMore && (
                    <div className="flex justify-center py-6">
                      <Loader className="w-5 h-5 text-green-500 animate-spin" />
                    </div>
                  )}

                  {!hasMore && filteredEntities.length > 0 && (
                    <div className="text-center py-4 text-white/50 text-sm">
                      No more businesses to load
                    </div>
                  )}
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MapPage;
